import { msg, T, Var, useMessages } from 'gt-react';
import { cn } from '@/lib/utils';
import { WordDiff } from '@/components/common/WordDiff';
import type {
  DiffFieldKey,
  SopDiffField,
  SopDiffLayout,
  SopDiffSection,
} from '../sopVersionDiff';

const DIFF_SIDES = ['before', 'after'] as const;

const FIELD_LABELS = {
  name: msg('Name', { context: 'SOP diff field' }),
  description: msg('Instruction', { context: 'SOP diff field' }),
  constraints: msg('Rules', { context: 'SOP diff field' }),
  kind: msg('Step Type', { context: 'SOP diff field' }),
  phase: msg('Phase', { context: 'SOP diff field' }),
  risk: msg('Risk', { context: 'SOP diff field' }),
  provenance: msg('Provenance', { context: 'SOP diff field' }),
  authorship: msg('Authorship', { context: 'SOP diff field' }),
  surface: msg('Surface', { context: 'SOP diff field' }),
  target: msg('Automation Target', { context: 'SOP diff field' }),
  action: msg('Automation Action', { context: 'SOP diff field' }),
  code: msg('Automation Code', { context: 'SOP diff field' }),
  verify: msg('Verification', { context: 'SOP diff field' }),
  branch: msg('Branch Definition', { context: 'SOP diff field' }),
  refSopId: msg('Embedded Procedure', { context: 'SOP diff field' }),
  refVersion: msg('Embedded Version', { context: 'SOP diff field' }),
  paramBindings: msg('Parameter Bindings', { context: 'SOP diff field' }),
  sensitive: msg('Sensitive', { context: 'SOP diff field' }),
  bestEffort: msg('Best Effort', { context: 'SOP diff field' }),
  sessionRules: msg('Session Rules', { context: 'SOP diff field' }),
  parameter: msg('Parameter Definition', { context: 'SOP diff field' }),
  bodyMarkdown: msg('Procedure Text', { context: 'SOP diff field' }),
} satisfies Record<DiffFieldKey, ReturnType<typeof msg>>;
const STATUS_LABELS = {
  added: msg('Added', { context: 'SOP diff status' }),
  removed: msg('Removed', { context: 'SOP diff status' }),
  modified: msg('Modified', { context: 'SOP diff status' }),
  unchanged: msg('Unchanged', { context: 'SOP diff status' }),
};

function DiffText({
  field,
  side,
}: {
  field: SopDiffField;
  side: 'before' | 'after';
}) {
  if (!field.changed) return <>{field[side]}</>;
  return <WordDiff before={field.before} after={field.after} side={side} />;
}
function DiffFieldView({
  field,
  layout,
}: {
  field: SopDiffField;
  layout: SopDiffLayout;
}) {
  const m = useMessages();
  const sides =
    field.changed || layout === 'split' ? DIFF_SIDES : (['after'] as const);
  return (
    <div className="border-t">
      <div className="bg-muted/40 px-3 py-1 text-xs font-medium">
        {m(FIELD_LABELS[field.key])}
      </div>
      <div className={cn('grid', layout === 'split' && 'grid-cols-2 divide-x')}>
        {sides.map((side) => (
          <div
            key={side}
            className={cn(
              'flex min-w-0 gap-2 p-3 text-sm',
              field.changed &&
                (side === 'before' ? 'bg-destructive/5' : 'bg-emerald-500/5'),
            )}
          >
            <span className="shrink-0 font-mono" aria-hidden="true">
              {field.changed ? (side === 'before' ? '−' : '+') : ' '}
            </span>
            <span className="sr-only">
              {side === 'before' ? (
                <T context="SOP diff original content">Before</T>
              ) : (
                <T context="SOP diff updated content">After</T>
              )}
            </span>
            <pre className="min-w-0 whitespace-pre-wrap break-words font-mono text-xs">
              <DiffText field={field} side={side} />
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}
function SectionFields({
  section,
  layout,
  showUnchanged,
}: {
  section: SopDiffSection;
  layout: SopDiffLayout;
  showUnchanged: boolean;
}) {
  if (showUnchanged || ['added', 'removed'].includes(section.status)) {
    return section.fields.map((field) => (
      <DiffFieldView key={field.key} field={field} layout={layout} />
    ));
  }
  const changed = section.fields.filter((field) => field.changed);
  const unchanged = section.fields.filter((field) => !field.changed);
  return (
    <>
      {changed.map((field) => (
        <DiffFieldView key={field.key} field={field} layout={layout} />
      ))}
      {unchanged.length > 0 && (
        <details className="border-t">
          <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground">
            <T context="SOP diff collapsed unchanged field count">
              <Var>{unchanged.length}</Var> Unchanged Fields
            </T>
          </summary>
          {unchanged.map((field) => (
            <UnchangedField key={field.key} field={field} layout={layout} />
          ))}
        </details>
      )}
    </>
  );
}
function UnchangedField({
  field,
  layout,
}: {
  field: SopDiffField;
  layout: SopDiffLayout;
}) {
  const m = useMessages();
  return (
    <details className="border-t">
      <summary className="cursor-pointer px-3 py-1 text-xs text-muted-foreground">
        {m(FIELD_LABELS[field.key])}
      </summary>
      <DiffFieldView field={field} layout={layout} />
    </details>
  );
}
function SectionHeading({ section }: { section: SopDiffSection }) {
  const m = useMessages();
  return (
    <span className="inline-flex w-[calc(100%-1.5rem)] flex-wrap items-center justify-between gap-2">
      <span>
        {section.kind === 'bodyMarkdown' || section.kind === 'sessionRules' ? (
          m(FIELD_LABELS[section.kind])
        ) : (
          <>
            {section.kind === 'parameter' && (
              <>
                <T context="SOP diff parameter heading">Parameter</T>:{' '}
              </>
            )}
            {section.kind === 'step' && (
              <>{section.afterPosition ?? section.beforePosition}. </>
            )}
            {section.name}
          </>
        )}
      </span>
      <span className="flex gap-2 text-xs font-normal text-muted-foreground">
        {!(section.moved && section.status === 'unchanged') &&
          m(STATUS_LABELS[section.status])}
        {section.moved && (
          <T context="SOP diff step or parameter moved position">
            Moved: <Var>{section.beforePosition}</Var> →{' '}
            <Var>{section.afterPosition}</Var>
          </T>
        )}
      </span>
    </span>
  );
}
export function SopVersionDiffView({
  sections,
  layout,
  showUnchanged,
}: {
  sections: SopDiffSection[];
  layout: SopDiffLayout;
  showUnchanged: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        layout === 'split' && 'min-w-[40rem]',
      )}
    >
      {sections.map((section) => (
        <details
          key={`${section.id}:${showUnchanged}`}
          open={
            showUnchanged || section.status !== 'unchanged' || section.moved
          }
          className="rounded-md border"
        >
          <summary className="cursor-pointer p-3 text-sm font-medium">
            <SectionHeading section={section} />
          </summary>
          <SectionFields
            section={section}
            layout={layout}
            showUnchanged={showUnchanged}
          />
        </details>
      ))}
    </div>
  );
}
